import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Tooltip,
  OutlinedInput,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import { MentionsInput, Mention } from "react-mentions";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { openMessage } from "../../../../store/slices/showMessageSlice";
import { bloqcibeApi } from "../../../../store/slices/apiSlice";
import { triggerRefetch } from "../../../../store/slices/querySlice";
import CustomButton from "../../../../components/@extended/CustomButton";
import { useTheme } from "@mui/material/styles";
import { getStatusColor, getPriorityColor } from "../../../util";
import { getNestApiErrorMessage } from "../../../../util/util";

const QueryDetail = ({ queryId, onBack, users, sponsorId, trialId, siteId, trialSiteId, fieldLabel, userSiteTrialRole }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const loggedInUser = useSelector((state) => state.userDetails.loggedInUser);
  const [getQuery] = bloqcibeApi.endpoints.getQuery.useLazyQuery();
  const [updateQuery, { isLoading: isUpdating }] = bloqcibeApi.endpoints.updateQuery.useMutation();
  const [addComment, { isLoading: isAddingComment }] = bloqcibeApi.endpoints.addQueryComment.useMutation();
  const [uploadCommentAttachments] = bloqcibeApi.endpoints.uploadQueryCommentAttachments.useMutation();
  const [addAssignee] = bloqcibeApi.endpoints.addQueryAssignee.useMutation();
  const [deleteAssignee] = bloqcibeApi.endpoints.deleteQueryAssignee.useMutation();
  const [downloadAttachment] = bloqcibeApi.endpoints.downloadQueryAttachment.useLazyQuery();

  const [query, setQuery] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("");
  const [comment, setComment] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [newAssignee, setNewAssignee] = useState("");
  const [isAddingAssignee, setIsAddingAssignee] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (queryId) {
      fetchQuery();
    }
  }, [queryId]);

  // Role-based access control helpers
  const canEditTitle = () => {
    return userSiteTrialRole !== 'site_coordinator';
  };

  const canEditDescription = () => {
    return userSiteTrialRole !== 'site_coordinator';
  };

  const canEditPriority = () => {
    return userSiteTrialRole !== 'site_coordinator';
  };

  // All possible statuses
  const allStatuses = ['OPEN', 'RESOLVED', 'VERIFIED', 'REOPENED'];
  
  // Get available (enabled) statuses based on role
  const getAvailableStatuses = () => {
    if (userSiteTrialRole === 'site_monitor' || userSiteTrialRole === 'data_manager') {
      return ['OPEN', 'VERIFIED', 'REOPENED'];
    } else if (userSiteTrialRole === 'site_coordinator' || userSiteTrialRole === 'PI') {
      return ['OPEN', 'RESOLVED'];
    }
    // Default: allow all statuses for other roles
    return ['OPEN', 'RESOLVED', 'VERIFIED', 'REOPENED'];
  };

  // Check if a status is enabled for the current role
  const isStatusEnabled = (statusValue) => {
    return getAvailableStatuses().includes(statusValue);
  };

  const fetchQuery = async () => {
    setIsLoading(true);
    try {
      const result = await getQuery(queryId);
      if (result.data) {
        const queryData = result.data;
        setQuery(queryData);
        setTitleValue(queryData.title || "");
        setDescriptionValue(queryData.description || "");
        setPriority(queryData.priority || "MEDIUM");
        setStatus(queryData.status || "OPEN");
      } else if (result.error) {
        dispatch(
          openMessage({
            message: getNestApiErrorMessage(result.error, "Failed to load query details"),
            messageSeverity: "error",
          })
        );
        if (onBack) {
          onBack();
        }
      }
    } catch (error) {
      console.error("Error fetching query:", error);
      dispatch(
        openMessage({
          message: getNestApiErrorMessage(error, "Failed to load query details"),
          messageSeverity: "error",
        })
      );
      if (onBack) {
        onBack();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateQuery = async ({status, priority, title, description}) => {
    // Role-based validation: site_coordinator cannot update title, description, or priority
    const updateData = {
      trialSiteId: trialSiteId,
      fieldLabel: fieldLabel,
    };

    if (status) {
      updateData.status = status;
    }
    if (canEditTitle() && title) {
      updateData.title = title;
    }
    if (canEditDescription() && description) {
      updateData.description = description;
    }
    if (canEditPriority() && priority) {
      updateData.priority = priority;
    }

    try {
      const result = await updateQuery({
        id: queryId,
        data: updateData,
      }).unwrap();
      if (result) {
        setQuery(result);
        setEditingTitle(false);
        setEditingDescription(false);
        dispatch(
          openMessage({
            message: "Query updated successfully",
            messageSeverity: "success",
          })
        );
        // Trigger refetch of queries in parent components
        dispatch(triggerRefetch());
      }
    } catch (error) {
      dispatch(
        openMessage({
          message: getNestApiErrorMessage(error, "Failed to update query"),
          messageSeverity: "error",
        })
      );
    }
  };

  const handleSaveTitle = () => {
    if (!titleValue.trim()) {
      setTitleValue(query?.title || "");
      setEditingTitle(false);
      return;
    }
    if (titleValue.length > 255) {
      dispatch(
        openMessage({
          message: "Title must not exceed 255 characters",
          messageSeverity: "error",
        })
      );
      return;
    }
    handleUpdateQuery({title: titleValue});
  };

  const handleSaveDescription = () => {
    if (descriptionValue.length > 2000) {
      dispatch(
        openMessage({
          message: "Description must not exceed 2000 characters",
          messageSeverity: "error",
        })
      );
      return;
    }
    handleUpdateQuery({description: descriptionValue});
  };

  const handleAddAssignee = async () => {
    if (!newAssignee) {
      return;
    }

    const selectedUser = users.find(u => String(u.userId) === String(newAssignee));
    if (!selectedUser) {
      dispatch(
        openMessage({
          message: "Selected user not found",
          messageSeverity: "error",
        })
      );
      return;
    }

    // Check if assignee already exists
    const existingAssignee = query?.assignees?.find(a => String(a.userId) === String(selectedUser.userId) || String(a.userId) === String(newAssignee));
    if (existingAssignee) {
      dispatch(
        openMessage({
          message: "This user is already assigned to the query",
          messageSeverity: "warning",
        })
      );
      setNewAssignee("");
      setIsAddingAssignee(false);
      return;
    }

    const userId = String(selectedUser.userId);

    try {
      await addAssignee({
        queryId,
        data: {
          userId,
          email: selectedUser.id || '',
          name: selectedUser.display || '',
          trialSiteId: trialSiteId,
          fieldLabel: fieldLabel,
        },
      }).unwrap();

      dispatch(
        openMessage({
          message: "Assignee added successfully",
          messageSeverity: "success",
        })
      );
      setNewAssignee("");
      setIsAddingAssignee(false);
      fetchQuery(); // Refresh query to show new assignee
      dispatch(triggerRefetch()); // Trigger refetch of queries in parent components
    } catch (error) {
      dispatch(
        openMessage({
          message: getNestApiErrorMessage(error, "Failed to add assignee"),
          messageSeverity: "error",
        })
      );
    }
  };

  const handleRemoveAssignee = async (userId) => {
    // Get current user's role from users array
    const currentUser = users.find(u => u.self === true);
    const currentUserRole = currentUser?.role;
    
    // Prevent removal if user role is 'site_monitor'
    if (currentUserRole === 'site_monitor') {
      dispatch(
        openMessage({
          message: "You do not have permission to remove assignees",
          messageSeverity: "error",
        })
      );
      return;
    }
    
    try {
      await deleteAssignee({
        queryId,
        userId,
      }).unwrap();

      dispatch(
        openMessage({
          message: "Assignee removed successfully",
          messageSeverity: "success",
        })
      );
      fetchQuery(); // Refresh query to show updated assignees
      dispatch(triggerRefetch()); // Trigger refetch of queries in parent components
    } catch (error) {
      dispatch(
        openMessage({
          message: getNestApiErrorMessage(error, "Failed to remove assignee"),
          messageSeverity: "error",
        })
      );
    }
  };

  // Get available users (not already assigned)
  const getAvailableUsers = () => {
    if (!query?.assignees || !users) return users || [];
    const assignedUserIds = query.assignees.map(a => String(a.userId));
    return users.filter(u => !assignedUserIds.includes(String(u.userId)));
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB

    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      const isValidType = allowedTypes.includes(file.type) || 
        file.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|pdf|doc|docx)$/);
      const isValidSize = file.size <= maxSize;

      if (!isValidType) {
        invalidFiles.push(`${file.name} - Invalid file type`);
      } else if (!isValidSize) {
        invalidFiles.push(`${file.name} - File size exceeds 5MB`);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      dispatch(
        openMessage({
          message: invalidFiles.join('; '),
          messageSeverity: "error",
        })
      );
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddComment = async () => {
    if (!comment.trim() && selectedFiles.length === 0) {
      return;
    }

    try {
      // If we have files, use the comments-with-attachments endpoint
      if (selectedFiles.length > 0 && uploadCommentAttachments) {
        const formData = new FormData();
        formData.append("comment", comment || "");
        if (trialSiteId) formData.append("trialSiteId", trialSiteId);
        if (fieldLabel) formData.append("fieldLabel", fieldLabel);
        selectedFiles.forEach((file) => {
          formData.append("files", file);
        });

        await uploadCommentAttachments({
          queryId,
          formData,
        }).unwrap();
      } else {
        // Add comment without attachments
        await addComment({
          queryId,
          data: {
            comment: comment || "",
            trialSiteId: trialSiteId,
            fieldLabel: fieldLabel,
          },
        }).unwrap();
      }

      setComment("");
      setSelectedFiles([]);
      fetchQuery(); // Refresh query to show new comment
      dispatch(triggerRefetch()); // Trigger refetch of queries in parent components
      dispatch(
        openMessage({
          message: "Comment added successfully",
          messageSeverity: "success",
        })
      );
    } catch (error) {
      dispatch(
        openMessage({
          message: getNestApiErrorMessage(error, "Failed to add comment"),
          messageSeverity: "error",
        })
      );
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const handleDownloadAttachment = async (attachmentId) => {
    try {
      const result = await downloadAttachment({
        attachmentId,
        queryId,
        sponsorId,
        trialId,
        siteId,
      });
      if (result.error) {
        const message =
          result.error.status === 403
            ? "This attachment does not belong to the current trial site"
            : getNestApiErrorMessage(result.error, "Failed to download attachment");
        dispatch(
          openMessage({
            message,
            messageSeverity: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        openMessage({
          message: getNestApiErrorMessage(error, "Failed to download attachment"),
          messageSeverity: "error",
        })
      );
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <Typography>Loading query details...</Typography>
      </Box>
    );
  }

  if (!query) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <Typography>Query not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "95%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, px:0, flexShrink: 0, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ minWidth: "auto", px: 1 }}>
          Back
        </Button>
      </Box>

      {/* Query Details */}
      <Box sx={{ flexGrow: 1, overflow: "auto", p: 2, px:0 }}>
        {/* Title Section */}
        <Box sx={{ mb: 3 }}>
          {editingTitle ? (
            <TextField
              fullWidth
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={handleSaveTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveTitle();
                } else if (e.key === "Escape") {
                  setTitleValue(query.title || "");
                  setEditingTitle(false);
                }
              }}
              autoFocus
              variant="outlined"
              size="small"
              inputProps={{ maxLength: 255 }}
              helperText={`${titleValue.length}/255 characters`}
              error={titleValue.length > 255}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                position: "relative",
                "&:hover": {
                  "& .edit-icon": {
                    opacity: canEditTitle() ? 1 : 0,
                  },
                },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                {query.title || "No title"}
              </Typography>
              {canEditTitle() && (
                <Tooltip title="Edit Title">
                  <IconButton
                    size="small"
                    onClick={() => setEditingTitle(true)}
                    className="edit-icon"
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s",
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Description
            </Typography>
          </Box>
          {editingDescription ? (
            <TextField
              fullWidth
              multiline
              rows={3}
              value={descriptionValue}
              onChange={(e) => setDescriptionValue(e.target.value)}
              onBlur={handleSaveDescription}
              variant="outlined"
              size="small"
              inputProps={{ maxLength: 1000 }}
              helperText={`${descriptionValue.length}/1000 characters`}
              error={descriptionValue.length > 1000}
            />
          ) : (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                position: "relative",
                bgcolor: theme.palette.grey[50],
                "&:hover": {
                  border: canEditDescription() ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                  "& .edit-icon": {
                    opacity: canEditDescription() ? 1 : 0,
                  },
                },
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ pr: canEditDescription() ? 4 : 0 }}>
                {query.description || "No description"}
              </Typography>
              {canEditDescription() && (
                <Tooltip title="Edit Description">
                  <IconButton
                    size="small"
                    onClick={() => setEditingDescription(true)}
                    className="edit-icon"
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                      opacity: 0,
                      transition: "opacity 0.2s",
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Priority and Status */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 120 }} disabled={!canEditPriority()}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priority}
              onChange={(e) => {
                setPriority(e.target.value);
                handleUpdateQuery({priority: e.target.value});
              }}
              label="Priority"
              disabled={!canEditPriority()}
              renderValue={(selected) => {
                const colors = getPriorityColor(selected);
                return (
                  <Chip
                    label={selected === 'LOW' ? 'Low' : selected === 'MEDIUM' ? 'Medium' : 'High'}
                    size="small"
                    sx={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      fontWeight: 500,
                      height: 24,
                    }}
                  />
                );
              }}
            >
              <MenuItem value="LOW">
                <Chip
                  label="Low"
                  size="small"
                  sx={{
                    backgroundColor: getPriorityColor('LOW').bg,
                    color: getPriorityColor('LOW').text,
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
              <MenuItem value="MEDIUM">
                <Chip
                  label="Medium"
                  size="small"
                  sx={{
                    backgroundColor: getPriorityColor('MEDIUM').bg,
                    color: getPriorityColor('MEDIUM').text,
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
              <MenuItem value="HIGH">
                <Chip
                  label="High"
                  size="small"
                  sx={{
                    backgroundColor: getPriorityColor('HIGH').bg,
                    color: getPriorityColor('HIGH').text,
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              onChange={(e) => {
                if (isStatusEnabled(e.target.value)) {
                  setStatus(e.target.value);
                  handleUpdateQuery({status: e.target.value});
                }
              }}
              label="Status"
              renderValue={(selected) => {
                const colors = getStatusColor(selected);
                const statusLabel = selected === 'OPEN' ? 'Open' : 
                                   selected === 'RESOLVED' ? 'Resolved' : 
                                   selected === 'VERIFIED' ? 'Verified' : 
                                   selected === 'REOPENED' ? 'Reopened' : selected;
                return (
                  <Chip
                    label={statusLabel}
                    size="small"
                    sx={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      fontWeight: 500,
                      height: 24,
                    }}
                  />
                );
              }}
            >
              {allStatuses.map((statusOption) => {
                const isEnabled = isStatusEnabled(statusOption);
                const colors = getStatusColor(statusOption);
                const statusLabel = statusOption === 'OPEN' ? 'Open' : 
                                   statusOption === 'RESOLVED' ? 'Resolved' : 
                                   statusOption === 'VERIFIED' ? 'Verified' : 
                                   statusOption === 'REOPENED' ? 'Reopened' : statusOption;
                return (
                  <MenuItem 
                    key={statusOption} 
                    value={statusOption}
                    disabled={!isEnabled}
                  >
                    <Chip
                      label={statusLabel}
                      size="small"
                      sx={{
                        backgroundColor: colors.bg,
                        color: colors.text,
                        fontWeight: 500,
                        opacity: isEnabled ? 1 : 0.9,
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        {/* Assignees Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Assignees
            </Typography>
            {!isAddingAssignee && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setIsAddingAssignee(true)}
                disabled={getAvailableUsers().length === 0}
              >
                Add Assignee
              </Button>
            )}
          </Box>
          
          {isAddingAssignee && (
            <Box sx={{ mb: 2, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: theme.palette.grey[50] }}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Assignee</InputLabel>
                <Select
                  value={newAssignee}
                  onChange={(e) => setNewAssignee(e.target.value)}
                  input={<OutlinedInput label="Select Assignee" />}
                >
                  {getAvailableUsers().map((user) => (
                    <MenuItem key={user.userId} value={String(user.userId)}>
                      {user.display || user.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: "flex", gap: 1, mt: 1, justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  onClick={() => {
                    setIsAddingAssignee(false);
                    setNewAssignee("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAddAssignee}
                  disabled={!newAssignee}
                >
                  Add
                </Button>
              </Box>
            </Box>
          )}

          {query.assignees && query.assignees.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {query.assignees.map((assignee) => {
                // Get current user's role from users array
                const currentUser = users.find(u => u.self === true);
                const currentUserRole = currentUser?.role;
                const canDelete = currentUserRole !== 'site_monitor';
                
                return (
                  <Chip
                    key={assignee.userId}
                    icon={<PersonIcon />}
                    label={assignee.name || assignee.email || assignee.userId}
                    onDelete={canDelete ? () => handleRemoveAssignee(assignee.userId) : undefined}
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 0.5 }}
                  />
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              No assignees
            </Typography>
          )}
        </Box>

        {/* Attached Files */}
        {query.attachments && query.attachments.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Attached Files
            </Typography>
            <List dense>
              {query.attachments.map((attachment) => (
                <ListItem key={attachment.id} sx={{ py: 0.5, px: 0 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2">{attachment.s3Key.split("/").pop()}</Typography>
                    }
                    secondary={
                      <Button
                        size="small"
                        onClick={() => handleDownloadAttachment(attachment.id)}
                      >
                        Download
                      </Button>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Comments Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{  fontWeight: 600 }}>
            Comments
          </Typography>
          {query.comments && query.comments.length > 0 ? (
            <List sx={{ px: 0 }}>
              {query.comments.map((commentItem, index) => {
                const commentUserInitials = commentItem.createdBy
                  ? commentItem.createdBy
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .substring(0, 2)
                  : "?";

                return (
                  <React.Fragment key={commentItem.id}>
                    <ListItem alignItems="flex-start" sx={{ py: 1, px: 0 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: "0.75rem", bgcolor: theme.palette.primary.main, mr: 2 }}>
                        {commentUserInitials}
                      </Avatar>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {commentItem.createdBy || "Unknown"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {moment(commentItem.createdAt).format("MM/DD/YYYY hh:mm a")}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            {commentItem.comment && (() => {
                              // Process @mentions, render username with background color
                              let comm = commentItem.comment;
                              const matches = comm.match(/\@\[(.*?)\]\([^)]+\)/g);
                              if (matches) {
                                for (let i = 0; i < matches.length; i++) {
                                  const match = matches[i].match(/\@\[(.*?)\]\((.*?)\)/);
                                  if (match) {
                                    const username = match[1];
                                    comm = comm.replace(
                                      match[0],
                                      `<span style='background-color: #e8edff; font-style: italic;'>${username}</span>`
                                    );
                                  }
                                }
                              }
                              return (
                                <Typography
                                  variant="body2"
                                  sx={{ mt: 1, mb: 1 }}
                                  component="div"
                                  dangerouslySetInnerHTML={{ __html: comm }}
                                />
                              );
                            })()}
                            {commentItem.attachments && commentItem.attachments.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                  Attachments:
                                </Typography>
                                {commentItem.attachments.map((attachment) => (
                                  <Button
                                    key={attachment.id}
                                    size="small"
                                    onClick={() => handleDownloadAttachment(attachment.id)}
                                    sx={{ mr: 1, mb: 0.5 }}
                                  >
                                    {attachment.s3Key.split("/").pop()}
                                  </Button>
                                ))}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < query.comments.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
              No comments yet
            </Typography>
          )}
        </Box>
      </Box>

      {/* Comment Input */}
      <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        {selectedFiles.length > 0 && (
          <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {selectedFiles.map((file, index) => (
              <Chip
                key={index}
                label={`${file.name} (${formatFileSize(file.size)})`}
                onDelete={() => handleRemoveFile(index)}
                size="small"
              />
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
          <MentionsInput
            style={{
              width: "100%",
              minHeight: 40,
              maxHeight: 100,
              input: {
                padding: 9,
                border: "1px solid silver",
                "&focused": {
                  border: `1px solid ${theme.palette.primary.main}`,
                },
              },
              suggestions: {
                list: {
                  backgroundColor: "white",
                  border: "1px solid rgba(0,0,0,0.15)",
                  fontSize: 14,
                },
                item: {
                  padding: "5px 15px",
                  borderBottom: "1px solid rgba(0,0,0,0.15)",
                  "&focused": {
                    backgroundColor: theme.palette.primary.lighter,
                  },
                },
              },
            }}
            value={comment}
            onChange={(event, newValue) => setComment(newValue)}
            forceSuggestionsAboveCursor
            allowSpaceInQuery
            allowSuggestionsAboveCursor
            placeholder={`Enter "@" to mention user`}
          >
            <Mention trigger="@" data={users || []} appendSpaceOnAdd />
          </MentionsInput>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: "none" }}
            id="comment-file-upload"
            accept="image/*,.pdf,.doc,.docx"
          />
          <label htmlFor="comment-file-upload">
            <IconButton component="span" size="small">
              <AttachFileIcon />
            </IconButton>
          </label>
          <CustomButton
            variant="contained"
            onClick={handleAddComment}
            disabled={isAddingComment || (!comment.trim() && selectedFiles.length === 0)}
          >
            <SendIcon />
          </CustomButton>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(QueryDetail);

